import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import '../styles/LuxuryDesignSystem.css';

const NavigationBilingual = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, switchLanguage, t } = useLanguage();
  
  const nav = translations.nav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  
  // Get current language prefix
  const langPrefix = `/${language}`;
  
  // Get route for current language
  const getRoute = (pageKey) => {
    const route = routeMapping[language][pageKey];
    return `${langPrefix}${route ? '/' + route : ''}`;
  };
  
  const isActive = (pageKey) => {
    const route = routeMapping[language][pageKey];
    const currentPath = location.pathname.replace(/^\/(en|fr)/, '').replace(/^\//, '');
    return currentPath === route;
  };
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };
  
  const otherLang = language === 'en' ? 'fr' : 'en';
  
  return (
    <>
      <nav style={{
        borderBottom: '1px solid var(--border-light)',
        padding: '24px 0',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backdropFilter: 'blur(12px)',
        backgroundColor: scrolled ? 'rgba(255, 254, 251, 0.98)' : 'rgba(255, 254, 251, 0.95)',
        boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.04)' : 'none',
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        overflow: 'visible'
      }}>
        <div className="container" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {/* Logo */}
          <Link to={getRoute('home')} style={{
            display: 'flex',
            flexDirection: 'column',
            textDecoration: 'none',
            lineHeight: 1.1,
            alignItems: 'center'
          }} onClick={closeMobileMenu}>
            <span style={{
              fontSize: '20px',
              fontWeight: 400,
              fontFamily: "'Suisse Regular', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              color: 'var(--text-primary)',
              letterSpacing: '0.25em',
              textTransform: 'uppercase'
            }}>
              Fenicis
            </span>
            <span style={{
              fontSize: '8px',
              fontWeight: 400,
              fontFamily: "'Suisse Regular', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              color: 'var(--text-secondary)',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginTop: '4px'
            }}>
              Global Hospitality
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <ul className="desktop-nav" style={{
            display: 'flex',
            listStyle: 'none',
            gap: '40px',
            margin: 0,
            padding: 0,
            alignItems: 'center'
          }}>
            <li>
              <Link 
                to={getRoute('home')} 
                className="navigation-text"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  color: 'var(--text-primary)'
                }}
              >
                {t(nav.home)}
                {isActive('home') && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'var(--text-primary)'
                  }} />
                )}
              </Link>
            </li>
            <li>
              <Link 
                to={getRoute('house')} 
                className="navigation-text"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  color: 'var(--text-primary)'
                }}
              >
                {t(nav.theHouse)}
                {isActive('house') && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'var(--text-primary)'
                  }} />
                )}
              </Link>
            </li>
            <li>
              <Link 
                to={getRoute('expertise')} 
                className="navigation-text"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  color: 'var(--text-primary)'
                }}
              >
                {t(nav.expertise)}
                {isActive('expertise') && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'var(--text-primary)'
                  }} />
                )}
              </Link>
            </li>
            <li>
              <Link 
                to={getRoute('works')} 
                className="navigation-text"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  color: 'var(--text-primary)'
                }}
              >
                {t(nav.selectedWorks)}
                {isActive('works') && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'var(--text-primary)'
                  }} />
                )}
              </Link>
            </li>
            <li>
              <Link 
                to={getRoute('founder')} 
                className="navigation-text"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  color: 'var(--text-primary)'
                }}
              >
                {t(nav.founder)}
                {isActive('founder') && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'var(--text-primary)'
                  }} />
                )}
              </Link>
            </li>
            <li>
              <Link 
                to={getRoute('contact')} 
                className="navigation-text"
                style={{
                  textDecoration: 'none',
                  position: 'relative',
                  color: 'var(--text-primary)'
                }}
              >
                {t(nav.contact)}
                {isActive('contact') && (
                  <span style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '100%',
                    height: '1px',
                    background: 'var(--text-primary)'
                  }} />
                )}
              </Link>
            </li>
            
            {/* Language Switcher */}
            <li style={{ marginLeft: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.1em'
              }}>
                <button
                  onClick={() => switchLanguage('en')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    color: language === 'en' ? 'var(--text-primary)' : 'var(--text-light)',
                    fontWeight: language === 'en' ? '600' : '400',
                    fontFamily: "'Suisse Regular', sans-serif",
                    fontSize: '12px',
                    letterSpacing: '0.1em',
                    textDecoration: language === 'en' ? 'underline' : 'none',
                    textUnderlineOffset: '4px'
                  }}
                >
                  EN
                </button>
                <span style={{ color: 'var(--text-light)' }}>|</span>
                <button
                  onClick={() => switchLanguage('fr')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    color: language === 'fr' ? 'var(--text-primary)' : 'var(--text-light)',
                    fontWeight: language === 'fr' ? '600' : '400',
                    fontFamily: "'Suisse Regular', sans-serif",
                    fontSize: '12px',
                    letterSpacing: '0.1em',
                    textDecoration: language === 'fr' ? 'underline' : 'none',
                    textUnderlineOffset: '4px'
                  }}
                >
                  FR
                </button>
              </div>
            </li>
          </ul>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMobileMenu}
            className="mobile-menu-btn"
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: 'var(--text-primary)',
              alignItems: 'center',
              gap: '8px'
            }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            <span style={{
              fontSize: '14px',
              fontFamily: "'Suisse Regular', sans-serif",
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}>{t(nav.menu)}</span>
          </button>
        </div>
      </nav>
      {/* Spacer for fixed nav */}
      <div style={{ height: '81px' }} />
      {mobileMenuOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#fffffb',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}>
          {/* Mobile Menu Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px',
            borderBottom: '1px solid var(--border-light)'
          }}>
            <Link to={getRoute('home')} style={{
              display: 'flex',
              flexDirection: 'column',
              textDecoration: 'none',
              lineHeight: 1.1,
              alignItems: 'center'
            }} onClick={closeMobileMenu}>
              <span style={{
                fontSize: '20px',
                fontWeight: 400,
                fontFamily: "'Suisse Regular', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                color: 'var(--text-primary)',
                letterSpacing: '0.25em',
                textTransform: 'uppercase'
              }}>Fenicis</span>
              <span style={{
                fontSize: '8px',
                fontWeight: 400,
                fontFamily: "'Suisse Regular', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                color: 'var(--text-secondary)',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                marginTop: '4px'
              }}>Global Hospitality</span>
            </Link>
            <button 
              onClick={closeMobileMenu}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              aria-label="Close menu"
            >
              <X size={24} />
              <span style={{
                fontSize: '14px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}>{t(nav.menu)}</span>
            </button>
          </div>
          
          {/* Mobile Menu Links */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '48px 24px',
            gap: '32px',
            flex: 1
          }}>
            <Link 
              to={getRoute('home')} 
              onClick={closeMobileMenu}
              style={{
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.05em'
              }}
            >
              {t(nav.home)}
            </Link>
            <Link 
              to={getRoute('house')} 
              onClick={closeMobileMenu}
              style={{
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.05em'
              }}
            >
              {t(nav.theHouse)}
            </Link>
            <Link 
              to={getRoute('expertise')} 
              onClick={closeMobileMenu}
              style={{
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.05em'
              }}
            >
              {t(nav.expertise)}
            </Link>
            <Link 
              to={getRoute('works')} 
              onClick={closeMobileMenu}
              style={{
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.05em'
              }}
            >
              {t(nav.selectedWorks)}
            </Link>
            <Link 
              to={getRoute('founder')} 
              onClick={closeMobileMenu}
              style={{
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.05em'
              }}
            >
              {t(nav.founder)}
            </Link>
            <Link 
              to={getRoute('contact')} 
              onClick={closeMobileMenu}
              style={{
                textDecoration: 'none',
                color: 'var(--text-primary)',
                fontSize: '18px',
                fontFamily: "'Suisse Regular', sans-serif",
                letterSpacing: '0.05em'
              }}
            >
              {t(nav.contact)}
            </Link>
            
            {/* Mobile Language Switcher */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginTop: '16px',
              paddingTop: '24px',
              borderTop: '1px solid var(--border-light)'
            }}>
              <button
                onClick={() => { switchLanguage('en'); closeMobileMenu(); }}
                style={{
                  background: language === 'en' ? 'var(--interactive-base)' : 'none',
                  color: language === 'en' ? '#fffffb' : 'var(--text-secondary)',
                  border: '1px solid var(--border-medium)',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontFamily: "'Suisse Regular', sans-serif",
                  cursor: 'pointer',
                  letterSpacing: '0.1em'
                }}
              >
                English
              </button>
              <button
                onClick={() => { switchLanguage('fr'); closeMobileMenu(); }}
                style={{
                  background: language === 'fr' ? 'var(--interactive-base)' : 'none',
                  color: language === 'fr' ? '#fffffb' : 'var(--text-secondary)',
                  border: '1px solid var(--border-medium)',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontFamily: "'Suisse Regular', sans-serif",
                  cursor: 'pointer',
                  letterSpacing: '0.1em'
                }}
              >
                Français
              </button>
            </div>
            
            <Link 
              to={getRoute('contact')}
              onClick={closeMobileMenu}
              className="btn-primary"
              style={{
                marginTop: 'auto',
                textAlign: 'center'
              }}
            >
              {t(nav.cta)}
            </Link>
          </div>
        </div>
      )}
      
      <style>{`
        @media (max-width: 900px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: flex !important;
          }
        }
      `}</style>
    </>
  );
};

export default NavigationBilingual;
